import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, AfterViewInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FedExLocationLookupDialog } from '../../models/dialog.model';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { DriverService } from '../../services/driver/driver.service';
import { DialogHandlerService } from '../../services/dialog-handler/dialog-handler.service';

@Component({
    standalone: false,
    selector: 'octo-fedex-location-lookup-dialog',
    templateUrl: './fedex-location-lookup-dialog.component.html',
    styleUrls: ['./fedex-location-lookup-dialog.component.scss'],
})
export class FedexLocationLookupDialogComponent implements OnInit, OnDestroy {
    octoGooglePlacesAutocomplete = {
        googleAddressOptions: {
            componentRestrictions: { country: 'US' },
        },
        formGroup: this.fb.group({
            address_line: [null, Validators.required],
            address_apartment_suite_no: [
                null,
                Validators.pattern('^((?!#).)*$'),
            ],
            address_city: [null, Validators.required],
            address_state: [null, Validators.required],
            address_zip: [null, [Validators.required]],
            address_country: ['US', Validators.required],
            complete_address: [null],
        }),
    };

    searchingFor: any;
    locations: any[] = [];
    private pacObserver: MutationObserver | null = null;
    private styleObservers = new Map<HTMLElement, MutationObserver[]>();
    private pacDismissed = false;
    private documentClickBound = false;

    private hideAllPacContainers(): void {
        document.querySelectorAll<HTMLElement>('.pac-container').forEach((pac) => {
            pac.style.setProperty('display', 'none', 'important');
            try { pac.hidePopover(); } catch { /* not open */ }
        });
    }

    private showActivePacContainer(): void {
        if (this.pacDismissed) {
            this.hideAllPacContainers();
            return;
        }
        const allPacs = Array.from(document.querySelectorAll<HTMLElement>('.pac-container[data-promoted]'));
        const withItems = allPacs.filter((p) => p.children.length > 0);
        if (!withItems.length) {
            this.hideAllPacContainers();
            return;
        }
        // Show only the last pac-container that has items; hide all others
        const active = withItems[withItems.length - 1];
        allPacs.forEach((pac) => {
            if (pac === active) {
                pac.style.setProperty('display', 'block', 'important');
                try { pac.showPopover(); } catch { /* already open */ }
            } else {
                pac.style.setProperty('display', 'none', 'important');
                try { pac.hidePopover(); } catch { /* not open */ }
            }
        });
    }

    private dismissPacContainers(): void {
        this.pacDismissed = true;
        this.hideAllPacContainers();
    }

    locationLookupPayload = {
        address_line: null,
        apartment_suite_no: null,
        city: null,
        state: null,
        country: 'US',
        zip_code: null,
        radius: 20,
        max_results: 75,
    };

    selectedLocation: any = null;

    fetchingLocations: boolean = false;
    confirmingLocation: boolean = false;

    /**
     * Constructor method.
     *
     * @param {MatDialogRef<ConfirmationDialogComponent>} modalRef - Dialog reference.
     * @param {FedExLocationLookupDialog} data - Dialog data.
     * @param {FormBuilder} fb - Form builder.
     * @param {DriverService} driverService - Driver service.
     * @param {DialogHandlerService} dialogService - Dialog service.
     * @param {ChangeDetectorRef} cdr - Change detector ref.
     */
    constructor(
        private modalRef: MatDialogRef<FedexLocationLookupDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: FedExLocationLookupDialog,
        private fb: UntypedFormBuilder,
        private driverService: DriverService,
        private dialogService: DialogHandlerService,
        private cdr: ChangeDetectorRef
    ) {
        this.searchingFor = data.location;

        this.locationLookupPayload = {
            address_line: data.location.split(' ').slice(0, -1).join(' '),
            apartment_suite_no: null,
            city: this.data?.address_city || null,
            state: this.data?.address_state || null,
            country: 'US',
            zip_code: this.searchingFor.split(' ').pop(),
            radius: 20,
            max_results: 75,
        };

        // Set search input value and initialize searchingFor
        this.searchingFor = data.location || '';
        this.octoGooglePlacesAutocomplete.formGroup.patchValue({
            address_line: this.searchingFor,
            complete_address: this.searchingFor,
        });

        this.octoGooglePlacesAutocomplete.formGroup.valueChanges.subscribe(
            (value) => {
                if (value.complete_address && 
                    (value.address_line || value.address_city || value.address_state || value.address_zip)) {
                    // Update from autocomplete selection
                    this.locationLookupPayload = {
                        address_line: value.address_line,
                        apartment_suite_no: value.address_apartment_suite_no,
                        city: value.address_city,
                        state: value.address_state,
                        country: 'US',
                        zip_code: value.address_zip,
                        radius: 20,
                        max_results: 75,
                    };
                    this.searchingFor = value.complete_address;
                    this.searchLocations();
                }
            }
        );
    }

    ngOnInit(): void {
        this.searchLocations();
    }

    /**
     * Set the selected location.
     */
    setSelectedLocation(event: any) {
        const locationId = event.value;
        this.selectedLocation = this.locations.find(
            (location) => location.location_id == locationId
        );
    }

    /**
     * Search for locations on search.
     */
    searchLocations() {
        this.fetchingLocations = true;
        this.locations = [];
        this.selectedLocation = null;
        this.cdr.detectChanges();

        this.driverService
            .getFedExLocations(this.locationLookupPayload)
            .subscribe(
                (response) => {
                    this.locations = response.data?.location_list || [];
                    this.locations = this.locations.map((location) => {
                        location.store_hours_display = this.condenseStoreHours(
                            location.store_hours
                        );
                        return location;
                    });
                    this.fetchingLocations = false;
                    this.cdr.detectChanges()
                },
                (error) => {
                    this.locations = [];
                    this.fetchingLocations = false;
                    this.cdr.detectChanges()

                    this.modalRef.close(error);
                }
            );
    }

    /**
     * Confirm the selected location.
     */
    confirmLocation() {

        this.confirmingLocation = true;

        this.driverService
            .selectFedExLocation({
                location_id: this.selectedLocation.location_id,
                location_name: this.selectedLocation.location_name,
                address: this.selectedLocation.address,
                email_address: this.selectedLocation.email_address,
                phone_number: this.selectedLocation.phone_number,
                distance: this.selectedLocation.distance,
                store_hours: this.selectedLocation.store_hours,
            })
            .subscribe((response) => {
                this.closeDialog(this.selectedLocation);
                // this.confirmingLocation = false;
                this.cdr.detectChanges();
            });
    }

    /**
     * merge days with similar opening hours
     * Input is a dictionary with key value pairs of days and hours.
     * Output is a string with the days and hours condensed.
     * e.g.
     * Input - Mon: 8:00AM-7:00PM, Tue: 8:00AM-7:00PM, Wed: 8:00AM-7:00PM, Thu: 8:00AM-7:00PM, Fri: 8:00AM-7:00PM, Sat: 9:00AM-6:00PM, Sun: 12:00PM-6:00PM
     * Output - "Mon-Fri: 8:00AM-7:00PM, Sat: 9:00AM-6:00PM, Sun: 12:00PM-6:00PM"
     *
     * @param {any} storeHours - Store hours.
     */
    condenseStoreHours(storeHours: any) {
        let collapsed = [];
        let idx = -1;
        for (const [key, value] of Object.entries(storeHours)) {
            if (idx == -1 || collapsed[idx].hours != value) {
                collapsed.push({ days: [key], hours: value });
                idx++;
            } else {
                collapsed[idx].days.push(key);
            }
        }

        let lines = [];
        for (let i = 0; i < collapsed.length; i++) {
            let line = collapsed[i];
            let days = line.days;
            if (days.length == 1) {
                lines.push(days[0] + ': ' + line.hours + '\n');
            } else {
                lines.push(
                    days[0] +
                        '-' +
                        days[days.length - 1] +
                        ': ' +
                        line.hours +
                        '\n'
                );
            }
        }

        let formattedHours = `<ul>`;
        for (let i = 0; i < lines.length; i++) {
            formattedHours += `<li>${lines[i]}</li>`;
        }
        formattedHours += `</ul>`;

        return formattedHours;
    }

    /**
     * Close the dialog.
     */
    closeDialog(location: any = null) {
        this.modalRef.close(location);
    }

    /**
     * Handle manual input changes
     */
    onSearchInputChange() {
        this.pacDismissed = false; // Allow pac-container to show again on new input
        this.changeCss();
        // Update the autocomplete form with manual input
        this.octoGooglePlacesAutocomplete.formGroup.patchValue({
            complete_address: this.searchingFor,
            address_line: this.searchingFor
        }, { emitEvent: false });
        this.cdr.detectChanges();
    }

    /**
     * Search locations when user types manually (not from autocomplete)
     */
    searchLocationsManually() {
        this.dismissPacContainers();

        if (this.searchingFor && this.searchingFor.trim()) {
            const parsedAddress = this.parseAddressFromString(this.searchingFor.trim());
            
            this.locationLookupPayload = {
                ...this.locationLookupPayload,
                address_line: parsedAddress.address_line,
                city: parsedAddress.city,
                state: parsedAddress.state,
                zip_code: parsedAddress.zip_code,
            };
            this.searchLocations();
        }
    }

    /**
     * Parse address components from a full address string
     */
    parseAddressFromString(fullAddress: string): any {
        let address_line = '';
        let city = '';
        let state = '';
        let zip_code = '';



        try {
            let singleAddress = fullAddress;
            if (fullAddress.includes(', USA,')) {
                singleAddress = fullAddress.split(', USA,')[0] + ', USA';
            }
            
            singleAddress = singleAddress.replace(/,\s*USA\s*$/i, '').trim();
            
            const parts = singleAddress.split(',').map(part => part.trim());

            if (parts.length >= 3) {
                address_line = parts[0]; 
                city = parts[1]; 
                
                const stateZipPart = parts[2];

                const stateZipMatch = stateZipPart.match(/^([A-Z]{2})\s+(\d{5}(-\d{4})?)$/i);
                
                if (stateZipMatch) {
                    state = stateZipMatch[1].toUpperCase(); 
                    zip_code = stateZipMatch[2]; 

                } else {
                    const fullStateMatch = stateZipPart.match(/^(.+?)\s+(\d{5}(-\d{4})?)$/);
                    if (fullStateMatch) {
                        state = fullStateMatch[1].trim();
                        zip_code = fullStateMatch[2];

                    } else {
                        const spaceParts = stateZipPart.split(/\s+/);
                        if (spaceParts.length >= 2) {
                            const lastPart = spaceParts[spaceParts.length - 1];
                            if (/^\d{5}(-\d{4})?$/.test(lastPart)) {
                                zip_code = lastPart;
                                state = spaceParts.slice(0, -1).join(' ').trim();

                            } else {
                                state = stateZipPart;

                            }
                        } else {
                            state = stateZipPart;

                        }
                    }
                }
            } else if (parts.length === 2) {
                const firstPart = parts[0];
                const secondPart = parts[1];
                const stateZipMatch = secondPart.match(/^([A-Z]{2})\s+(\d{5}(-\d{4})?)$/i);
                
                if (stateZipMatch) {
                    city = firstPart;
                    state = stateZipMatch[1].toUpperCase();
                    zip_code = stateZipMatch[2];
                } else {
                    const fullStateMatch = secondPart.match(/^(.+?)\s+(\d{5}(-\d{4})?)$/);
                    if (fullStateMatch) {
                        city = firstPart;
                        state = fullStateMatch[1].trim();
                        zip_code = fullStateMatch[2];
                    } else {
                        address_line = firstPart;
                        city = secondPart;
                    }
                }
            } else if (parts.length === 1) {
                const singlePart = parts[0];
                const cityStateZipMatch = singlePart.match(/^(.+?),?\s+([A-Z]{2})\s+(\d{5}(-\d{4})?)$/i);
                
                if (cityStateZipMatch) {
                    city = cityStateZipMatch[1].replace(/,$/, '').trim();
                    state = cityStateZipMatch[2].toUpperCase();
                    zip_code = cityStateZipMatch[3];
                } else {
                    address_line = singlePart;
                }
            }
        } catch (error) {
            const simpleParts = fullAddress.replace(/,\s*USA\s*$/i, '').split(',').map(p => p.trim());
            if (simpleParts.length >= 3) {
                address_line = simpleParts[0];
                city = simpleParts[1];
                const lastPart = simpleParts[2];
                const match = lastPart.match(/([A-Z]{2})\s+(\d{5})$/i);
                if (match) {
                    state = match[1].toUpperCase();
                    zip_code = match[2];
                } else {
                    address_line = fullAddress;
                }
            } else {
                address_line = fullAddress;
            }
        }

        const result = {
            address_line: address_line,
            city: city,
            state: state,
            zip_code: zip_code
        };
        
        return result;
    }

    ngOnDestroy(): void {
        this.pacObserver?.disconnect();
        this.pacObserver = null;
        this.styleObservers.forEach((observers, pac) => {
            observers.forEach((obs) => obs.disconnect());
            pac.remove();
        });
        this.styleObservers.clear();
    }

    changeCss(): void {
        if (this.pacObserver) return;

        const promotePacContainer = (pac: HTMLElement): void => {
            if (pac.dataset['promoted']) return;
            pac.dataset['promoted'] = '1';
            pac.dataset['fedexModal'] = '1';
            pac.setAttribute('popover', 'manual');
            pac.style.margin = '0';

            // Start hidden
            pac.style.setProperty('display', 'none', 'important');
            try { pac.hidePopover(); } catch { /* not open */ }

            // On pac-item click → dismiss (hide and don't show again until input change)
            pac.addEventListener('mousedown', (e) => {
                const target = e.target as HTMLElement;
                if (target.closest('.pac-item')) {
                    setTimeout(() => this.dismissPacContainers(), 0);
                }
            });

            // Watch for Google adding/removing children → show or hide
            const childObs = new MutationObserver(() => this.showActivePacContainer());
            childObs.observe(pac, { childList: true });

            this.styleObservers.set(pac, [childObs]);
        };

        // Outside click → dismiss
        if (!this.documentClickBound) {
            this.documentClickBound = true;
            document.addEventListener('mousedown', (e) => {
                const target = e.target as HTMLElement;
                if (target.closest('.pac-container') || target.closest('#dialog-fedexlocation-search-input') || target.closest('#fedex-lookup-search-input')) {
                    return;
                }
                this.dismissPacContainers();
            });
        }

        document.querySelectorAll<HTMLElement>('.pac-container').forEach(promotePacContainer);

        this.pacObserver = new MutationObserver(() => {
            document.querySelectorAll<HTMLElement>('body > .pac-container:not([data-promoted])').forEach(promotePacContainer);
        });
        this.pacObserver.observe(document.body, { childList: true });

        const inputEl = document.querySelector<HTMLElement>('#dialog-fedexlocation-search-input, #fedex-lookup-search-input');
        if (inputEl && !inputEl.dataset['pacListener']) {
            inputEl.dataset['pacListener'] = '1';
            inputEl.addEventListener('input', () => {
                this.pacDismissed = false;
                this.showActivePacContainer();
            });
        }
    }
}
